import { regionEn } from "@/lib/seo-keywords";
// /en/translate/$language/in/$location — EN long-tail combo for translation
// language × expat-relevant Thailand location. Mirrors the TH route but in
// English, scoped to enLocations only (excluded from sitemap if not eligible).
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
} from "@/data/translation-languages";
import { getLocation, locations } from "@/data/locations";
import { TRANSLATION_DOCTYPES } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedServicesInLocation } from "@/components/RelatedServicesInLocation";
import {
  Languages, MapPin, FileCheck2, ShieldCheck, BadgeCheck, Clock,
  Phone, MessageCircle, ArrowRight, Globe2,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// Allow-list mirrors sitemap-en.xml — keeps the public surface coherent
// and avoids generating thin EN pages for upcountry agent-only locations.
const EN_LOCATION_TYPES = new Set([
  "headquarters",
  "branch",
  "bkk-district",
  "transit",
  "mall",
  "expat-hub",
]);

export const Route = createFileRoute("/en/translate/$language/in/$location")({
  loader: ({ params }) => {
    const lang = getTranslationLanguage(params.language);
    const loc = getLocation(params.location);
    if (!lang || !loc) throw notFound();
    if (!EN_LOCATION_TYPES.has(loc.type)) throw notFound();
    return { langSlug: params.language, locSlug: params.location };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const loc = getLocation(params.location);
    if (!l || !loc) return { meta: [{ title: ctaTitle("Page not found", "/en/translate/$language/in/$location", "en")}] };
    const url = `${SITE}/en/translate/${l.slug}/in/${loc.slug}`;
    const thPath = `/translate/${l.slug}/in/${loc.slug}`;
    const enPath = `/en/translate/${l.slug}/in/${loc.slug}`;
    const title = `${l.nameEn} Certified Translation in ${loc.nameEn} — from THB ${l.priceFromTh.toLocaleString()}/page | NYC`;
    const desc = `Certified ${l.nameEn} translation for clients in ${loc.nameEn}, Thailand — MoJ${l.isNaati ? " + NAATI": ""} sworn translators, MFA & embassy legalization in one engagement. ${l.turnaround}. Door-to-door pickup available. Talk to a specialist today.`;
    const faqs = [
      {
        q: `How much does ${l.nameEn} translation cost in ${loc.nameEn}?`,
        a: `From THB ${l.priceFromTh.toLocaleString()} per A4 page (≤250 words), certified by sworn translators with NYC stamp — accepted by embassies, consulates, MFA and Thai courts. ${l.turnaround} turnaround. We pick up and deliver documents to ${loc.nameEn}.`,
      },
      {
        q: `How do clients in ${loc.nameEn} send documents?`,
        a: `Three ways — (1) Scan or photograph via LINE @nycli for a 15-minute quote, (2) Walk-in at our HQ at Lat Phrao 95 (Bangkok) or branches in Khon Kaen / Udon Thani / Nong Khai, or (3) EMS / Messenger door-to-door across Thailand.`,
      },
      ...l.faqs.slice(0, 2).map((f) => ({ q: f.q, a: f.a })),
    ];

    const ratingKey = `en-translate/${l.slug}/${loc.slug}`;
    const rating = aggregateRatingNode(ratingKey);

    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/$language/in/$location", "en") },
        { name: "description", content: ctaDesc(desc, "/en/translate/$language/in/$location", "en")},
        { name: "keywords", content: [
          `${l.nameEn} translation ${loc.nameEn}`,
          `certified ${l.nameEn} translation Thailand`,
          `sworn ${l.nameEn} translator ${loc.nameEn}`,
          `MFA legalization ${loc.nameEn}`,
          ...l.keywords.slice(0, 3),
        ].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/translate/$language/in/$location", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/translate/$language/in/$location", "en")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:image", content: dynamicOgImage({
          title: `${l.nameEn} Translation · ${loc.nameEn}`,
          subtitle: `Certified · MFA · Apostille — from THB ${l.priceFromTh.toLocaleString()}/page`,
          badge: `${l.nameEn.toUpperCase()} · ${loc.nameEn.toUpperCase()}`,
        }) },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/en/translate/$language/in/$location", "en")},
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Certified ${l.nameEn} translation in ${loc.nameEn}`,
              serviceType: "Certified document translation",
              areaServed: {
                "@type": "Place",
                name: loc.nameEn,
                ...(loc.lat && loc.lng
                  ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
                  : {}),
              },
              availableLanguage: [
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
                { "@type": "Language", name: "Thai", iso6391: "th" },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: l.priceFromTh,
                priceCurrency: "THB",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: l.priceFromTh,
                  priceCurrency: "THB",
                  unitText: "page",
                },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LegalService",
              "@id": `${url}#localbusiness`,
              name: `NYC Legal & Notary Service — ${l.nameEn} translation (${loc.nameEn})`,
              url,
              telephone: "+66832494999",
              priceRange: "฿฿",
              address: {
                "@type": "PostalAddress",
                streetAddress: loc.address ?? "61 Lat Phrao Soi 95",
                addressLocality: loc.nameEn,
                postalCode: loc.postalCode ?? "10310",
                addressCountry: "TH",
              },
              areaServed: { "@type": "Place", name: loc.nameEn },
              openingHours: "Mo-Sa 09:00-18:00",
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Translation", item: `${SITE}/en/translate` },
                { "@type": "ListItem", position: 3, name: `${l.nameEn} Translation`, item: `${SITE}/en/translate/${l.slug}` },
                { "@type": "ListItem", position: 4, name: loc.nameEn, item: url },
              ],
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 2).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: EnTranslateLangLocationPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/translate" className="mt-4 inline-block text-gold underline">
        Back to translation hub
      </Link>
    </main>
  ),
});

function EnTranslateLangLocationPage() {
  const { langSlug, locSlug } = Route.useLoaderData() as { langSlug: string; locSlug: string };
  const l = getTranslationLanguage(langSlug)!;
  const loc = getLocation(locSlug)!;
  const others = TRANSLATION_LANGUAGES.filter((x) => x.slug !== langSlug).slice(0, 8);
  const nearbyLocations = locations
    .filter((x) => x.slug !== locSlug && x.region === loc.region && EN_LOCATION_TYPES.has(x.type))
    .slice(0, 8);
  const popularDocs = TRANSLATION_DOCTYPES.slice(0, 9);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate" className="hover:text-gold">Translation</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/$language" params={{ language: l.slug }} className="hover:text-gold">{l.nameEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{loc.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> Certified · {l.nameEn} · {loc.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {l.nameEn} Certified Translation for {loc.nameEn} — Ready to file
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            NYC Legal & Notary Service — sworn {l.nameEn} translators registered with the Ministry of Justice{l.isNaati ? " and NAATI" : ""}, with door-to-door pickup in {loc.nameEn}. From THB {l.priceFromTh.toLocaleString()}/page, ready in {l.turnaround}.
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `THB ${l.priceFromTh.toLocaleString()}/page` },
              { k: "Turnaround", v: l.turnaround },
              { k: "Service area", v: loc.nameEn },
              { k: "Rating", v: "4.9 / 2,147 reviews" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> Send via LINE @nycli
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) delivers certified {l.nameEn} translation to clients in {loc.nameEn} — sworn translators stamp every page, prices start at THB {l.priceFromTh.toLocaleString()}, turnaround {l.turnaround}, and translations are accepted by embassies, consulates, the Ministry of Foreign Affairs and Thai courts. Door-to-door pickup and delivery available across {regionEn(loc.region)}.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <MapPin className="inline h-6 w-6 text-amber-600" /> {l.nameEn} translation in {loc.nameEn}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            Clients in {loc.nameEn} can engage NYC three ways — scan via LINE @nycli for a 15-minute quote, walk-in at our {loc.region === "กรุงเทพมหานคร" ? "Lat Phrao 95 headquarters" : "nearest branch"}, or use EMS/Messenger pickup nationwide. {loc.description ?? ""}
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Common {l.nameEn} documents we translate</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {l.commonDocs.map((doc) => (
              <li key={doc} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {doc}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Destination countries</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Starting price · {loc.nameEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {l.nameEn} translation — THB {l.priceFromTh.toLocaleString()}/page
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Sworn-translator stamp + signature on every page</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Ready in {l.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> EMS pickup across {regionEn(loc.region)}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Notary + MFA + embassy chain handled in one engagement</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[
              {
                q: `How much is ${l.nameEn} translation in ${loc.nameEn}?`,
                a: `From THB ${l.priceFromTh.toLocaleString()}/page — certified by sworn translators, accepted by embassies / consulates / MFA. ${l.turnaround}.`,
              },
              {
                q: `How do clients in ${loc.nameEn} send documents?`,
                a: `Scan via LINE @nycli (quote in 15 minutes), walk-in at our office, or use EMS/Messenger pickup nationwide.`,
              },
              ...l.faqs.slice(0, 2).map((f) => ({ q: f.q, a: f.a })),
            ]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            Pick a document type → {l.nameEn} translation
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {popularDocs.map((d) => (
              <Link
                key={d.slug}
                to="/en/translate/$language/$doctype"
                params={{ language: l.slug, doctype: d.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{d.nameEn}</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{d.nameEn}</div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                </div>
                <div className="mt-1 text-[11px] text-slate-500">From THB {(l.priceFromTh + d.surcharge).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>

        {nearbyLocations.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Nearby locations · {l.nameEn} translation</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {nearbyLocations.map((n) => (
                <Link
                  key={n.slug}
                  to="/en/translate/$language/in/$location"
                  params={{ language: l.slug, location: n.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{regionEn(n.region)}</div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {l.nameEn} · {n.nameEn}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other languages · {loc.nameEn}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/en/translate/$language/in/$location"
                params={{ language: o.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{o.nameEn}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {o.nameEn} · {loc.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">From THB {o.priceFromTh.toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <RelatedServicesInLocation currentService="translate" locationSlug={locSlug} locale="en" />
      <RelatedComparisons category="translate" locale="en" />
      <TrustLayer variant="compact" />
    </main>
  );
}
